import { AlertTriangle, ShieldCheck, Lightbulb, CloudRain } from 'lucide-react';

export const autorouteArticle = {
  slug: 'autoroute-moto-regles-2026',
  weekVolume: 25,
  weekYear: 2026,
  monthLabel: 'Juillet 2026',
  category: 'Grand route',
  shortTitle: "L'autoroute en moto",
  title: "L'autoroute en moto",
  kicker: 'Règles probatoire • BAU • Pluie',
  subtitle: "Vitesses spécifiques, voies réservées, BAU et procédures de panne : ce qu'il faut savoir avant de prendre l'autoroute en 2026.",
  heroImage: '/images/articles/autoroute-sunset.jpg?v=20260514',
  heroImageAlt: 'Silhouette de motard sur moto sportive en mouvement sur autoroute au coucher du soleil — ambiance grand route',
  topRightKicker: { line1: 'Conseil ECSR', line2: 'Grand route' },
  readTimeMin: 5,
  seoDescription: "Autoroute en moto : vitesse probatoire, BAU, panne, pluie. Règles complètes 2026 par les moniteurs ECSR.",
  seoKeywords: 'autoroute moto, BAU moto, panne moto autoroute, vitesse probatoire moto, pluie autoroute',
  source: { label: 'Sécurité routière', url: 'https://www.securite-routiere.gouv.fr', refs: 'Articles R413-2 et R412-7 du Code de la route.' },
  chapo: {
    kicker: 'Vue d’ensemble',
    body: "L'autoroute concentre des règles spécifiques pour la moto : <strong>vitesse abaissée</strong> en période probatoire, <strong>BAU strictement réservée</strong> aux secours, <strong>distances de sécurité doublées</strong> en pluie. Une infraction = amende lourde + points retirés.",
    sideStat: { kicker: 'Probatoire', big: '110 km/h', body: "Vitesse maximale en autoroute pour les <strong>jeunes permis</strong> (3 ans normal / 2 ans AAC). <strong>130 km/h interdit</strong>.", accent: '#cf2031' },
  },
  pullQuote: { text: "La BAU n'est PAS une voie de circulation. Même pour doubler un bouchon, t'y engager te coûte 135 € et 3 points.", source: "Article R412-8 du Code de la route" },
  sections: [
    {
      part: 'Partie 1 — Les vitesses',
      title: 'Quels seuils en 2026 ?',
      compareCards: [
        {
          accent: '#cf2031', kicker: 'Permis probatoire', title: 'Limites abaissées',
          intro: "Pendant 3 ans (ou 2 ans après AAC) :",
          points: [
            { ok: false, text: 'Autoroute : <strong>110 km/h max</strong> (au lieu de 130)' },
            { ok: false, text: 'Voie rapide / 4 voies : <strong>100 km/h max</strong> (au lieu de 110)' },
            { ok: false, text: 'Route classique : <strong>80 km/h max</strong> (au lieu de 80-90 selon route)' },
            { ok: false, text: 'Disque « A » obligatoire à l’arrière du véhicule' },
          ],
        },
        {
          accent: '#16a34a', kicker: 'Permis confirmé', title: 'Limites standard',
          intro: "Après la période probatoire :",
          points: [
            { ok: true, text: 'Autoroute sec : <strong>130 km/h</strong>' },
            { ok: true, text: 'Autoroute pluie : <strong>110 km/h</strong> (-20 km/h obligatoire)' },
            { ok: true, text: 'Voie rapide : 110 km/h sec / 100 pluie' },
            { ok: true, text: 'Brouillard < 50 m de visibilité : 50 km/h max' },
          ],
        },
      ],
    },
    {
      part: 'Partie 2 — La BAU',
      title: 'La Bande d’Arrêt d’Urgence (BAU)',
      paragraphs: [
        "La <strong>BAU n’est PAS une voie de circulation</strong>. Elle est réservée aux <strong>véhicules en panne, accidents et secours</strong>.",
      ],
      cards: [
        { icon: AlertTriangle, accent: '#cf2031', kicker: 'Sanction', title: 'Circulation sur BAU', body: "Amende <strong>4ᵉ classe 135 €</strong> + retrait de <strong>3 points</strong>. Même pour 100 mètres dans un bouchon." },
        { icon: ShieldCheck, accent: '#16a34a', kicker: 'Cas autorisés', title: 'Quand utiliser la BAU', body: "<strong>Panne, accident, malaise</strong>. Toujours <strong>3 flèches successives</strong> + warnings + sortie complete sur la BAU." },
      ],
      cardsCols: 2,
    },
    {
      part: 'Partie 3 — En cas de panne',
      title: 'La procédure officielle',
      orderedList: [
        "<strong>Décélère progressivement</strong>, mets les <strong>warnings</strong>.",
        "<strong>Sors complètement</strong> sur la BAU — ne reste pas sur la voie.",
        "<strong>Mets ton gilet jaune</strong> AVANT de descendre de la moto.",
        "<strong>Recule</strong> derrière le rail de sécurité — <strong>jamais sur la chaussée</strong>.",
        "<strong>Appelle le 112</strong> ou utilise une <strong>borne d’appel</strong> orange (tous les 2 km).",
        "<strong>Attends</strong> derrière le rail — ne pas tenter de réparer toi-même en bord d’autoroute.",
      ],
      callouts: [
        { icon: AlertTriangle, accent: '#cf2031', body: "<strong>Triangle de présignalisation INTERDIT en moto</strong> (par mesure de sécurité — le motard ne doit jamais traverser les voies)." },
      ],
    },
    {
      part: 'Partie 4 — Pluie & visibilité',
      title: 'Adapter sa conduite',
      callouts: [
        { icon: CloudRain, accent: '#1d4ed8', body: "<strong>Pluie :</strong> -20 km/h sur autoroute (130 → 110), -10 km/h sur voie rapide. Distance de sécurité <strong>doublée</strong> (4 secondes au lieu de 2)." },
          { icon: AlertTriangle, accent: '#cf2031', body: "<strong>Brouillard épais (visibilité < 50 m) :</strong> 50 km/h max sur tout le réseau, même autoroute. Feux de croisement + antibrouillards arrière obligatoires." },
      ],
    },
  ],
  quiz: [
    { id: 'q1', question: "Quelle est la vitesse autoroute maximale pendant la période probatoire ?", options: ['90 km/h', '100 km/h', '110 km/h', '130 km/h'], correctIndex: 2, explain: "110 km/h pendant 3 ans (ou 2 après AAC). Disque 'A' obligatoire à l’arrière du véhicule." },
    { id: 'q2', question: 'Quelle est la sanction pour circulation sur la BAU ?', options: ['Avertissement', '90 € + 1 point', '135 € + 3 points', '375 € + 6 points'], correctIndex: 2, explain: "Article R412-8 : 135 € + 3 points. Même pour quelques mètres dans un bouchon." },
    { id: 'q3', question: "En cas de pluie, quelle vitesse autoroute pour permis confirmé ?", options: ['130 km/h', '120 km/h', '110 km/h', '100 km/h'], correctIndex: 2, explain: "-20 km/h obligatoire en pluie : 130 → 110 km/h. Distance de sécurité doublée en parallèle." },
    { id: 'q4', question: 'Que faire en cas de panne sur autoroute en moto ?', options: ["Poser un triangle de présignalisation à 30 mètres", 'Sortir sur la BAU + gilet + reculer derrière le rail + appeler 112', "Rester sur la moto en faisant des appels de phares", "Pousser la moto au prochain péage"], correctIndex: 1, explain: "Triangle interdit en moto (sécurité). On sort sur BAU, met le gilet, recule derrière le rail, appelle le 112 ou borne orange." },
    { id: 'q5', question: "Quelle vitesse autoroute par brouillard épais (visibilité < 50 m) ?", options: ['90 km/h', '70 km/h', '50 km/h', '30 km/h'], correctIndex: 2, explain: "50 km/h MAX sur tout le réseau, autoroute incluse. Feux de croisement + antibrouillards arrière obligatoires." },
  ],
  ctaFinal: {
    kicker: 'Pour aller plus loin',
    title: 'Distances de sécurité',
    body: "L'autoroute exige des distances doublées par mauvais temps. Maîtrise la règle des 2 secondes pour rouler sans stress.",
    primary: { label: 'Règle des 2 secondes', to: '/magazine/distances-securite-moto' },
    secondary: { label: 'Sanctions & barèmes', to: '/sanctions-baremes' },
  },
};
